import { useCallback, useEffect, useRef, useState } from "react";
import { PolicyManagementCounts } from "./types";
import {
  // TODO: re-enable when History chips return
  // getPolicyHistoryCount,
  PolicyHistoryCountDto,
  getPolicyTransactionCount,
} from "utils/api/policyManagementCountsApi";

type PageScope = "all" | "national" | "nonNational";

type Args = {
  scope: PageScope;
  pageStates: string[];
};

const EMPTY_COUNTS: PolicyManagementCounts = {
  qrTotal: 0,
  pcqTotal: 0,
  cqTotal: 0,
  historyTotal: 0,

  qrAll: 0,
  qrHasErrors: 0,
  qrOnHold: 0,
  qrLocked: 0,
  qrEdited: 0,
  qrUnedited: 0,

  pcqAll: 0,
  pcqPassed: 0,
  pcqForcePromoted: 0,
  pcqOnHold: 0,
  pcqLocked: 0,
  pcqEdited: 0,
  pcqUnedited: 0,
  pcqAdjustments: 0,

  cqAll: 0,
  cqForcePromoted: 0,
  cqEdited: 0,
  cqUnedited: 0,

  historyAll: 0,
  historyReg: 0,
  historySi: 0,
  historyAdj: 0,
  historyClosed: 0,
  historySent: 0,
  historyReconciled: 0,
  historyEdited: 0,
  historyUnedited: 0,
};

// "all" → undefined so the client omits the param and the server sees NULL,
// matching the stored procedure's expectation for the "no filter" case.
const SCOPE_TO_AGENCY_TYPE: Record<PageScope, string | undefined> = {
  all: undefined,
  national: "National",
  nonNational: "Non-National",
};

/**
 * Returns the flat PolicyManagementCounts payload for the current page-level
 * filter state. Fetches from `GET /api/PolicyInformationTransaction/GetPolicyTransactionCount`
 * and re-fetches when scope / pageStates change. Stale responses are dropped
 * via a request-id ref.
 */
export function usePolicyManagementCounts(args: Args): {
  counts: PolicyManagementCounts;
  refetch: () => void;
} {
  const [counts, setCounts] = useState<PolicyManagementCounts>(EMPTY_COUNTS);
  const [reloadToken, setReloadToken] = useState(0);
  const reqIdRef = useRef(0);
  const pageStatesKey = args.pageStates.join(",");

  // Re-fetch the counts on demand (e.g. after a force-promote succeeds).
  const refetch = useCallback(() => setReloadToken((t) => t + 1), []);

  useEffect(() => {
    const myReq = ++reqIdRef.current;
    const agencyType = SCOPE_TO_AGENCY_TYPE[args.scope];
    // Empty state list → undefined → axios omits the param → server sees NULL.
    const stateCodeList = pageStatesKey === "" ? undefined : pageStatesKey;
    Promise.all([
      getPolicyTransactionCount(agencyType, stateCodeList),
      // TODO: re-enable when History chips return
      // getPolicyHistoryCount(),
      Promise.resolve(null as PolicyHistoryCountDto | null),
    ]).then(([txDto, historyDto]) => {
      if (reqIdRef.current !== myReq) return; // stale response, drop
      if (!txDto && !historyDto) return;      // both failed: keep previous counts
      setCounts({
        qrTotal: txDto?.QualityReviewCount ?? 0,
        qrAll: txDto?.QualityReviewCount ?? 0,
        qrHasErrors: txDto?.QualityReviewErrorCount ?? 0,
        qrOnHold: txDto?.QualityReviewHoldCount ?? 0,
        qrLocked: txDto?.QualityReviewLockedCount ?? 0,
        qrEdited: txDto?.QualityReviewEditCount ?? 0,
        qrUnedited: txDto?.QualityReviewUneditCount ?? 0,

        pcqTotal: txDto?.PreCloseCount ?? 0,
        pcqAll: txDto?.PreCloseCount ?? 0,
        pcqPassed: txDto?.PreClosePassedCount ?? 0,
        pcqForcePromoted: txDto?.PreCloseForcePromotedCount ?? 0,
        pcqOnHold: txDto?.PreCloseHoldCount ?? 0,
        pcqLocked: txDto?.PreCloseLockedCount ?? 0,
        pcqEdited: txDto?.PreCloseEditCount ?? 0,
        pcqUnedited: txDto?.PreCloseUneditCount ?? 0,
        pcqAdjustments: txDto?.PreCloseAdjCount ?? 0,

        cqTotal: txDto?.CloseCount ?? 0,
        cqAll: txDto?.CloseCount ?? 0,
        cqForcePromoted: txDto?.CloseForcePromotedCount ?? 0,
        cqEdited: txDto?.CloseEditCount ?? 0,
        cqUnedited: txDto?.CloseUneditCount ?? 0,

        historyTotal: txDto?.HistoryCount ?? 0,
        historyAll: txDto?.HistoryCount ?? 0,
        historyReg: 0, // not provided by API yet
        historySi: historyDto?.SimultaneousCount ?? 0,
        historyAdj: historyDto?.AdjCount ?? 0,
        historyClosed: 0, // not provided by API yet
        historySent: 0, // not provided by API yet
        historyReconciled: 0,
        historyEdited: historyDto?.EditCount ?? 0,
        historyUnedited: historyDto?.UneditCount ?? 0,
      });
    });
  }, [args.scope, pageStatesKey, reloadToken]);

  return { counts, refetch };
}
